/**
 * Data-processing (GDPR) consent clause.
 *
 * EU resumes — Polish ones in particular — close with an explicit clause
 * authorising the recruiter to process the candidate's personal data. It is a
 * legal statement, not a resume section: it carries no heading, it is set as
 * fine print, and it always comes last.
 *
 * Two things live here:
 *
 *   1. `normalizeConsent` — the storage shape (`{ enabled, text }`), coerced
 *      defensively because it round-trips through `Report.content`, a JSON blob
 *      that predates this field and that AI passes rewrite wholesale.
 *   2. `detectConsentClause` — recovering an existing clause from the raw text
 *      of an uploaded resume, so an import pre-enables the box and keeps the
 *      candidate's own wording. That wording matters: a Polish clause typically
 *      cites the Act of 10 May 2018 alongside the GDPR, and replacing it with
 *      our generic default would quietly weaken a statement the candidate chose.
 */

import type { ResumeConsent, ResumeContent, ResumeCustomSection } from './types';

/**
 * Wording used when a user ticks the box themselves. Deliberately the
 * jurisdiction-neutral form: we cannot know which national act applies, and the
 * GDPR reference alone is valid across the EU.
 */
export const DEFAULT_CONSENT_TEXT =
  'I hereby give my consent to the processing of my personal data for the purposes necessary to carry out the recruitment process, in accordance with Regulation (EU) 2016/679 (GDPR).';

/** Long enough to be a clause rather than a stray fragment. */
const MIN_CLAUSE_LENGTH = 40;

/** Upper bound on both stored and detected text. The longest real clauses
 *  (Polish, citing two instruments in full) run to roughly 600 characters. */
export const MAX_CONSENT_TEXT_LENGTH = 2000;

/**
 * How far past the trigger phrase we look for confirmation that the sentence is
 * about personal data. Wide enough for `I hereby give my consent to the
 * processing of my personal data`, narrow enough that an unrelated later
 * sentence cannot vouch for the trigger.
 */
const SIGNAL_WINDOW = 240;

/** Refuse to swallow more than this into a clause; see `findClauseEnd`. */
const MAX_CLAUSE_CHARS = 1400;

/**
 * Phrases that open a consent clause.
 *
 * The trigger doubles as the clause's start offset, because every real phrasing
 * begins with the act of consenting ("I hereby give my consent…", "Wyrażam
 * zgodę…"). That matters for PDF imports: extracted text is frequently one
 * run-on blob with no paragraph breaks to anchor to — in the fixture this was
 * built against the clause sits mid-blob, immediately after a `LANGUAGE SKILLS`
 * heading that the extractor merged into the same line.
 *
 * Diacritic-free spellings are listed alongside the correct ones because PDF
 * extraction drops accents on some fonts.
 */
const CONSENT_TRIGGER = new RegExp(
  [
    // English
    'i\\s+(?:hereby\\s+)?(?:give|grant|provide|express)\\s+(?:my\\s+|the\\s+|)consent',
    'i\\s+hereby\\s+consent',
    'i\\s+consent\\s+to\\s+the\\s+processing',
    'i\\s+agree\\s+to\\s+the\\s+processing',
    'consent\\s+to\\s+the\\s+processing\\s+of',
    // Polish
    'niniejszym\\s+wyra[żz]am\\s+zgod[ęe]',
    'wyra[żz]am\\s+zgod[ęe]',
    'zgadzam\\s+si[ęe]\\s+na\\s+przetwarzanie',
    // German
    'hiermit\\s+erkl(?:ä|ae)re\\s+ich\\s+mich\\s+einverstanden',
    'ich\\s+willige\\s+ein',
    // Ukrainian / Russian
    '(?:даю|надаю)\\s+(?:свою\\s+)?згоду',
    'даю\\s+(?:свое|своё)?\\s*согласие',
  ].join('|'),
  'giu'
);

/**
 * Confirmation that the trigger really is a data-processing consent and not,
 * say, "I consent to relocation". Required within `SIGNAL_WINDOW` of the
 * trigger — this is what keeps the broad `I agree to the processing` style
 * triggers from firing on unrelated sentences.
 */
const DATA_SIGNAL = new RegExp(
  [
    'personal\\s+data',
    'my\\s+data',
    'data\\s+processing',
    'processing\\s+of\\s+(?:my|the|personal)',
    '\\bgdpr\\b',
    '\\brodo\\b',
    '2016\\s*/\\s*679',
    'dan(?:e|ych)\\s+osobow(?:e|ych)',
    'personenbezogene[nr]?\\s+daten',
    'datenschutz',
    'персональних\\s+даних',
    'особистих\\s+даних',
    'персональных\\s+данных',
  ].join('|'),
  'iu'
);

function toText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\r\n?/g, '\n');
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Find where the clause that starts at `start` ends.
 *
 * Scans for a sentence-final `.` that is *outside* any parentheses, because the
 * legal citations these clauses carry are parenthesised and full of periods
 * ("…item 1000, and in accordance with Regulation (EU) 2016/679 … (GDPR))."):
 * the first depth-0 period is the real end, every period before it is inside a
 * citation. A blank line ends the clause too, for imports that preserved
 * paragraphs.
 *
 * Returns `null` when neither is found within `MAX_CLAUSE_CHARS`. Bailing out is
 * deliberate — enabling the checkbox with a paragraph of unrelated resume text
 * pasted into a legal statement is worse than not detecting the clause at all.
 * Running off the end of the document is fine, though: a clause that is the last
 * line of a resume and carries no final period is common.
 */
function findClauseEnd(text: string, start: number): number | null {
  let depth = 0;

  for (let i = start; i < text.length; i += 1) {
    if (i - start > MAX_CLAUSE_CHARS) return null;

    const char = text[i];

    if (char === '(' || char === '[') {
      depth += 1;
      continue;
    }
    if (char === ')' || char === ']') {
      if (depth > 0) depth -= 1;
      continue;
    }
    if (depth > 0) continue;

    // A blank line always closes the clause.
    if (char === '\n' && /^\n\s*\n/.test(text.slice(i))) {
      return i;
    }

    if (char !== '.' && char !== '!' && char !== '?') continue;

    const rest = text.slice(i + 1);
    if (!rest.trim()) return i + 1;

    // A real sentence break is followed by whitespace and something that can
    // open a new sentence. This is what stops `Dz. Ustaw` or `art. 6` style
    // abbreviations from cutting the clause short when they fall outside a
    // citation's parentheses.
    const boundary = /^\s+(\S)/.exec(rest);
    if (!boundary) continue;
    const nextChar = boundary[1];
    if (/[a-z0-9]/i.test(nextChar) && nextChar === nextChar.toLowerCase()) continue;

    return i + 1;
  }

  return text.length;
}

/**
 * Extract a data-processing consent clause from free resume text.
 *
 * Returns the clause verbatim (whitespace collapsed, so a clause wrapped across
 * lines by a PDF extractor comes back as the single sentence it is), or `null`
 * when the text carries no such clause.
 *
 * When several passages qualify, the last one wins: a consent clause belongs at
 * the foot of a resume, whereas the plausible false positive — a lawyer's or
 * DPO's bullet describing when a data subject must consent to processing —
 * appears in the experience section above it.
 */
export function detectConsentClause(source: unknown): string | null {
  const text = toText(source);
  if (!text.trim()) return null;

  let start: number | null = null;

  CONSENT_TRIGGER.lastIndex = 0;
  for (let match = CONSENT_TRIGGER.exec(text); match !== null; match = CONSENT_TRIGGER.exec(text)) {
    if (match[0].length === 0) {
      CONSENT_TRIGGER.lastIndex += 1;
      continue;
    }
    if (DATA_SIGNAL.test(text.slice(match.index, match.index + SIGNAL_WINDOW))) {
      start = match.index;
    }
  }

  if (start === null) return null;

  const end = findClauseEnd(text, start);
  if (end === null) return null;

  const clause = collapseWhitespace(text.slice(start, end));
  if (clause.length < MIN_CLAUSE_LENGTH || clause.length > MAX_CONSENT_TEXT_LENGTH) return null;

  return clause;
}

/** True when `value` is, in its entirety, a consent clause. */
export function isConsentOnlyText(value: unknown): boolean {
  const text = collapseWhitespace(toText(value));
  if (!text) return false;

  const clause = detectConsentClause(text);
  if (!clause) return false;

  // Allow a leading label ("Consent:", "RODO") and trailing punctuation, but
  // nothing substantial: a section that merely *mentions* consent keeps its
  // content.
  return clause.length >= text.length - 24;
}

/**
 * Coerce a stored `consent` value into the canonical shape.
 *
 * Everything here is defensive on purpose: the value comes back from a JSON
 * column written by several producers (the builder, two AI passes) and from
 * resumes saved before this field existed, where it is simply absent.
 */
export function normalizeConsent(value: unknown): ResumeConsent {
  if (!value || typeof value !== 'object') {
    return { enabled: false, text: '' };
  }

  const record = value as Record<string, unknown>;
  const text = typeof record.text === 'string' ? record.text.slice(0, MAX_CONSENT_TEXT_LENGTH) : '';

  return {
    enabled: record.enabled === true,
    text,
  };
}

/** The clause the resume will print, or `''` when it prints none. */
export function resolveConsentText(consent: ResumeConsent | undefined): string {
  if (!consent?.enabled) return '';
  return consent.text.trim();
}

/**
 * Pre-fill consent from the text of an uploaded resume.
 *
 * Only ever turns the flag on, and never rewrites wording that is already
 * there — an existing clause is the candidate's, whether they typed it or a
 * previous import found it.
 *
 * Also drops a custom section that the AI extractor created out of nothing but
 * the clause, which would otherwise print the same sentence twice: once under a
 * spurious "Consent" heading and once as the fine print at the foot.
 */
export function applyDetectedConsent(content: ResumeContent, sourceText: unknown): ResumeContent {
  const existing = normalizeConsent(content.consent);
  const clause = detectConsentClause(sourceText);

  const consent: ResumeConsent = existing.text.trim()
    ? existing
    : clause
      ? { enabled: true, text: clause }
      : existing;

  if (!consent.enabled) {
    return { ...content, consent };
  }

  const customSections = Array.isArray(content.customSections)
    ? content.customSections.filter(
        (section: ResumeCustomSection) => !isConsentOnlyText(section?.content)
      )
    : content.customSections;

  return { ...content, consent, customSections };
}
